import { ActionKeys } from '~/actions/constants';
import * as CSVImportActions from '~/actions/csv-import.actions';
import { sendGTMEvent } from '~/actions/gtm.actions';
import { sendTrackEvent } from '~/actions/prod-analytics.actions';
import { setIdVerificationStatus } from '~/actions/user.actions';
import { AxiosResponse } from 'axios';
import { push } from 'connected-react-router';
import { CSV_IMPORT_SUCCESS } from '~/constants/gtm';
import { call, put, takeLatest } from 'redux-saga/effects';
import * as csvImportService from '~/services/csv-import.service';
import { getVerificationStatus } from '~/services/user.service';
import * as CSVMapping from '~/types/CSVMapping';
import * as CSVValue from '~/types/CSVValue';
import * as IDVerification from '~/types/IDVerification';
import { ORDER_TRACKING_EVENT } from '~/utils/TrackingHelper/types';
import { trackOrderUpdate } from '~/utils/TrackingHelper/utils';

export function* uploadFile(action: ReturnType<typeof CSVImportActions.uploadCSV>) {
  try {
    const result: AxiosResponse = yield call(csvImportService.uploadCSV, action.payload.file);
    const payload = {
      fileUrl: result.data.file_url,
      userValues: result.data.user_values.map(CSVValue.fromJSON),
      mapping: CSVMapping.fromJSON(result.data.mapping),
    };
    yield put(CSVImportActions.uploadCSVSuccess(payload));
    yield put(push({ pathname: `/orders/csv-import/step-two` }));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((err as any).response && (err as any).response.status === 403) {
      // get verification status
      const idvResult: AxiosResponse<IDVerification.JSONIDVerification> =
        yield call(getVerificationStatus);
      const verification = IDVerification.fromJSON(idvResult.data);
      // render appropriate modal
      yield put(setIdVerificationStatus(verification));
    } else {
      const errorTitle = 'We were unable to upload your CSV file';
      const errorMessage = 'uploadMessage';
      yield put(CSVImportActions.uploadCSVError(errorMessage, errorTitle));
    }
  }
}

export function* fetchTemplates() {
  try {
    const result: AxiosResponse<CSVMapping.JSONCSVMapping[]> = yield call(
      csvImportService.getTemplates,
    );

    const payload = {
      templates: result.data.map(CSVMapping.fromJSON),
    };

    yield put(CSVImportActions.fetchCSVTemplatesSuccess(payload));
  } catch (err) {
    const errorTitle = 'We were unable to fetch your CSV templates';
    const errorMessage = 'refreshMessage';
    yield put(CSVImportActions.fetchCSVTemplatesError(errorMessage, errorTitle));
  }
}

export function* saveTemplate(action: ReturnType<typeof CSVImportActions.saveCSVTemplate>) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filteredMappings = (action.payload.template as any).fieldMappings.filter(
      (fieldMapping: { field: { id: number } }) => fieldMapping.field.id !== 0,
    );
    const payload = {
      field_mappings: filteredMappings.map(CSVMapping.fieldMappingToJSON),
      name: action.payload.name,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      id: (action.payload.template as any).id,
      is_active: action.payload.isActive,
    };
    const result: AxiosResponse = yield call(csvImportService.saveTemplate, payload);

    yield put(
      CSVImportActions.saveCSVTemplateSuccess(
        action.payload.fileUrl!,
        result.data.template_id,
        action.payload.trackingReference,
      ),
    );
  } catch (err) {
    const errorTitle = 'We are unable to save your CSV template';
    const errorMessage = 'refreshMessage';
    yield put(CSVImportActions.saveCSVTemplateError(errorMessage, errorTitle));
  }
}

export function* deleteTemplate(action: ReturnType<typeof CSVImportActions.deleteCSVTemplate>) {
  try {
    const payload = {
      template_id: action.payload.templateId,
    };
    yield call(csvImportService.deleteTemplate, payload);

    const result: AxiosResponse<CSVMapping.JSONCSVMapping[]> = yield call(
      csvImportService.getTemplates,
    );

    const successPayload = {
      templates: result.data.map(CSVMapping.fromJSON),
      deletedTemplateId: action.payload.templateId,
    };
    yield put(CSVImportActions.deleteCSVTemplateSuccess(successPayload));
  } catch (err) {
    const errorTitle = 'Error occured deleting CSV template';
    const errorMessage = 'deleteMessage';
    yield put(CSVImportActions.deleteCSVTemplateError(errorMessage, errorTitle));
  }
}

export function* importOrders(action: ReturnType<typeof CSVImportActions.importCSVOrders>) {
  try {
    const payload = {
      csv_url: action.payload.fileUrl,
      template_id: action.payload.templateId,
    };
    yield call(csvImportService.importCSVOrders, payload);
    yield put(CSVImportActions.importCSVOrdersSuccess());
    trackOrderUpdate(
      action.payload.trackingReference,
      ORDER_TRACKING_EVENT.operations.IMPORT_STARTED,
      {},
    );
    yield put(sendTrackEvent('CSV Import'));
    yield put(
      sendGTMEvent({
        eventName: CSV_IMPORT_SUCCESS,
      }),
    );
  } catch (err) {
    const errorTitle = "We can't read your CSV file";
    const errorMessage = 'uploadMessage';
    yield put(CSVImportActions.importCSVOrdersError(errorMessage, errorTitle));
  }
}

export default [
  takeLatest(ActionKeys.CSV_IMPORT_FILE_UPLOAD, uploadFile),
  takeLatest(ActionKeys.CSV_IMPORT_FETCH_TEMPLATES, fetchTemplates),
  takeLatest(ActionKeys.CSV_IMPORT_SAVE_TEMPLATE, saveTemplate),
  takeLatest(ActionKeys.CSV_IMPORT_DELETE_TEMPLATE, deleteTemplate),
  takeLatest(ActionKeys.CSV_IMPORT_SAVE_TEMPLATE_SUCCESS, importOrders),
];
